import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AccountConfigService } from 'src/app/services/account-config.service';
import { CommonService } from 'src/app/services/common-services/common.service';

@Component({
  selector: 'app-copy-settings',
  templateUrl: './copy-settings.component.html',
  styleUrls: ['./copy-settings.component.scss'],
})
export class CopySettingsComponent implements OnInit {
  userInfo: any;
  // Entity lists
  sourceEntities: any[] = [];
  destinationEntities: any[] = [];
  // Selected entities
  selectedSourceEntity: number | null = null;
  selectedDestinationEntities: number[] = [];


  // Settings select all flags
  orgSelectAll: boolean = false;
  workflowSelectAll: boolean = false;
  configSelectAll: boolean = false;
  allSelectAll: boolean = false;


  // Settings lists
  organizationSettings: any[] = [
    { id: 1, name: 'Categories', selected: false, apiKey: 'copyCategories', type: 'organization' },
    { id: 2, name: 'Merchants', selected: false, apiKey: 'copyMerchants', type: 'organization' }
  ];

  workflowSettings: any[] = [
    { id: 3, name: 'Approval Workflows', selected: false, apiKey: 'copyAprovalWorkflows', type: 'workflow' },
    { id: 5, name: 'General Policies', selected: false, apiKey: 'copyGeneralPolicies', type: 'workflow' },
    { id: 6, name: 'Approval Policies', selected: false, apiKey: 'copyApprovalPolicies', type: 'workflow' },
    { id: 10, name: 'Tax Code', selected: false, apiKey: 'copyTaxCodes', type: 'workflow' },
    { id: 9, name: 'HSN Code', selected: false, apiKey: 'copyHsnCodes', type: 'workflow' },
    { id: 8, name: 'Finance Integration Code', selected: false, apiKey: 'copyFinanceIntegrationCodes', type: 'workflow' },
    { id: 7, name: 'Budgets VS Actuals', selected: false, apiKey: 'copyBudgetVsActuals', type: 'workflow' },
    { id: 4, name: 'Budget Policies', selected: false, apiKey: 'copyBudgetPolicies', type: 'workflow' },
  ];

  configSettings: any[] = [
    { id: 11, name: 'General Configurations', selected: false, apiKey: 'copyGeneralConfiguration', type: 'config' },
    { id: 12, name: 'Email Configurations', selected: false, apiKey: 'copyEmailConfiguration', type: 'config' },
    { id: 13, name: 'Banners', selected: false, apiKey: 'copyBanners', type: 'config' },
    { id: 14, name: 'Custom Fields', selected: false, apiKey: 'copyCustomFields', type: 'config' },
    { id: 15, name: 'City Groups', selected: false, apiKey: 'copyCityGroups', type: 'config' },
    { id: 16, name: 'Perdiem Types', selected: false, apiKey: 'copyPerDiemTypes', type: 'config' }
  ];

  constructor(
    private commonService: CommonService, private accountConfigService: AccountConfigService,
    private translate: TranslateService, public toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    // Load entities (in a real app, this would come from an API)
    this.getLegalEntities();
    this.updateSelectAllStates();
  }

  getLegalEntities() {
    let obj = {
      companyId: this.userInfo.companyId,
      companyGuid: this.userInfo.companyGuId,
      searchText: '',
      pageSize: null,
      pageNumber: null
    }

    this.commonService.getAllLegalEntities(obj).subscribe(data => {
      this.sourceEntities = data?.payload?.legalEntityList || [];
      this.destinationEntities = data?.payload?.legalEntityList || [];
      this.sourceEntities = this.sourceEntities.map(entity => ({
        id: entity.legalEntityId,
        code: entity.legalEntityCode,
        name: entity.legaEntityName
      }));
      this.destinationEntities = this.destinationEntities.map(entity => ({
        id: entity.legalEntityId,
        code: entity.legalEntityCode,
        name: entity.legaEntityName
      }));
    }, error => {
      console.error('Error fetching legal entities:', error);
      // Handle error appropriately here
      
    });
  }

  // Toggle individual setting
  toggleSetting(setting: any): void {
    setting.selected = !setting.selected;
    this.updateSelectAllStates();
  }


  // Toggle organization select all
  toggleOrgSelectAll(): void {
    this.orgSelectAll = !this.orgSelectAll;
    this.organizationSettings.forEach(setting => {
      setting.selected = this.orgSelectAll;
    });
    this.updateSelectAllStates();
  }


  // Toggle workflow select all
  toggleWorkflowSelectAll(): void {
    this.workflowSelectAll = !this.workflowSelectAll;
    this.workflowSettings.forEach(setting => {
      setting.selected = this.workflowSelectAll;
    });
    this.updateSelectAllStates();
  }


  // Toggle config select all
  toggleConfigSelectAll(): void {
    this.configSelectAll = !this.configSelectAll;
    this.configSettings.forEach(setting => {
      setting.selected = this.configSelectAll;
    });
    this.updateSelectAllStates();
  }


  // Toggle all select all
  toggleAllSelectAll(): void {
    this.allSelectAll = !this.allSelectAll;

    // Update all section toggles
    this.orgSelectAll = this.allSelectAll;
    this.workflowSelectAll = this.allSelectAll;
    this.configSelectAll = this.allSelectAll;

    // Update all settings
    this.organizationSettings.forEach(setting => {
      setting.selected = this.allSelectAll;
    });
    this.workflowSettings.forEach(setting => {
      setting.selected = this.allSelectAll;
    });
    this.configSettings.forEach(setting => {
      setting.selected = this.allSelectAll;
    });
  }


  // Update select all states based on individual settings
  updateSelectAllStates(): void {
    this.orgSelectAll = this.organizationSettings.every(setting => setting.selected);
    this.workflowSelectAll = this.workflowSettings.every(setting => setting.selected);
    this.configSelectAll = this.configSettings.every(setting => setting.selected);

    // Master select all is only true if all sections are selected
    this.allSelectAll = this.orgSelectAll && this.workflowSelectAll && this.configSelectAll;
  }

  // Save settings
  copyingSettings: boolean = false;
  saveSettings(): void {
    if(!this.selectedSourceEntity && this.selectedDestinationEntities.length == 0) {
      this.toastr.error('Please select source and destination entities', this.translate.instant("alerts.error", { timeOut: 3000 }));
      return;
    } else if(!this.selectedSourceEntity) {
      this.toastr.error('Please select source entity', this.translate.instant("alerts.error", { timeOut: 3000 }));
      return;
    } else if(this.selectedDestinationEntities.length == 0) {
      this.toastr.error('Please select destination entities', this.translate.instant("alerts.error", { timeOut: 3000 }));
      return;
    }
    this.copyingSettings = true;
    const obj = {
      sourceLegalEntityId: this.selectedSourceEntity,
      destinationLegalEntityIds: this.selectedDestinationEntities,
      organizartion: {},
      workflowsAndPolicies: {},
      configuration: {}
    };
  
    this.organizationSettings.forEach(setting => {
      obj.organizartion[setting.apiKey] = setting.selected;
    });
  
    this.workflowSettings.forEach(setting => {
      obj.workflowsAndPolicies[setting.apiKey] = setting.selected;
    });
  
    this.configSettings.forEach(setting => {
      obj.configuration[setting.apiKey] = setting.selected;
    });

    this.accountConfigService.copySettings(obj).subscribe((res) => {
      this.copyingSettings = false;
      this.toastr.success('Copied Settings Succesfully', this.translate.instant("alerts.succes"));
    }, (error) => {
      this.copyingSettings = false;
      this.toastr.error(error, this.translate.instant("alerts.error", { timeOut: 3000 }));
    });
    
  }
}